import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useYieldCallback } from '@zillow/yield-callback';
import type { SavedListing } from 'app/shared/halo/contexts/SavedListingsContext';
import { SavedListingCard } from '../SavedListingCard';
import { Button } from '../../../ui/Button';
import AppActions from 'app/shared/flux/actions/AppActions';
import UserItemActions from 'app/shared/flux/actions/UserItemActions';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import type { ListingDetails } from 'app/types/listingDetails.type';
import { closeShortlistSheet } from 'app/shared/halo/stores/shortlistSheetStore';
import * as styles from './shortlistContent.styles';

const toSavedListing = (listing: ListingDetails): SavedListing => ({
  id: listing.maloneLotIdEncoded,
  href: listing.uriV2,
  title: listing.displayName || listing.address?.street || '',
  price: listing.listingMinMaxPriceBeds?.minPrice ?? 0,
  imageUrl: listing.photos?.[0]?.url || listing.medPhotoUrls?.[0] || listing.previewPhoto?.url || '',
  bedDisplay: listing.listingMinMaxPriceBeds?.bedDisplaySemiCompact,
  bathDisplay: listing.listingMinMaxPriceBeds?.bathDisplaySemiCompact,
  neighborhood: listing.address?.neighborhood,
  topAmenities: listing.amenities?.amenities?.slice(0, 2),
  latitude: listing.geo?.lat,
  longitude: listing.geo?.lon,
  savedAt: listing.userItemDates?.favorite?.date ?? Date.now(),
});

export const ShortlistContent = () => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state: RootReduxState) => state.user.loggedIn);
  const favorites = useSelector((state: RootReduxState) => state.listings.listingGroups.favorite);
  const [isLoading, setIsLoading] = useState(false);
  const yieldNavigate = useYieldCallback((path: string) => {
    closeShortlistSheet();
    window.router.transitionTo(path);
  });

  useEffect(() => {
    if (!isLoggedIn) return;
    setIsLoading(true);
    void (async () => {
      try {
        await (dispatch as Function)(UserItemActions.getUserItems('favorite', 0, 50, ''));
      } finally {
        setIsLoading(false);
      }
    })();
  }, [dispatch, isLoggedIn]);

  const handleRemove = (id: string) => {
    const listing = favorites.find((l) => l.maloneLotIdEncoded === id);
    if (listing) {
      dispatch(UserItemActions.removeUserItem('favorite', listing));
    }
  };

  if (!isLoggedIn) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyStateTitle}>Sign in to save your favorites</div>
        <div className={styles.emptyStateText}>
          Keep track of listings you love and pick up right where you left off.
        </div>
        <div className={styles.signInButton}>
          <Button
            tone="brand"
            emphasis="filled"
            size="md"
            onClick={() =>
              dispatch(
                AppActions.showPopupModal('PromptLogin', {
                  caption: 'Sign in or register to keep track of your favorite listings!',
                  shouldNotRedirect: true,
                }),
              )
            }
          >
            Sign in / Register
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyStateText}>Loading your saved homes…</div>
      </div>
    );
  }

  if (favorites.length === 0) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyStateTitle}>No saved homes yet</div>
        <div className={styles.emptyStateText}>Tap the heart on a listing to save it to your shortlist.</div>
      </div>
    );
  }

  const savedListings = [...favorites].reverse().map(toSavedListing);

  return (
    <div className={styles.container}>
      <div className={styles.subtitle}>
        {favorites.length} saved {favorites.length === 1 ? 'home' : 'homes'}
      </div>
      <div className={styles.listContainer}>
        {savedListings.map((listing) => (
          <SavedListingCard
            key={listing.id}
            listing={listing}
            onRemove={handleRemove}
            onClick={listing.href ? () => yieldNavigate(listing.href) : undefined}
          />
        ))}
      </div>
    </div>
  );
};
